/* eslint-disable @angular-eslint/prefer-inject --
 * Constructor parameter injection is intentional: it lets specs instantiate
 * the tracker directly with stubs and skip Angular's TestBed (which would
 * otherwise pull in NgZone / Router internals our stubs don't model).
 */
import { Injectable, type OnDestroy } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  type Event as RouterEvent,
} from '@angular/router';
import type { Span, Tracer } from '@opentelemetry/api';
import { SpanStatusCode } from '@opentelemetry/api';
import type { Subscription } from 'rxjs';

import { SPA_OTEL_ATTR, SPA_PERF_SPAN_PREFIX } from './metrics.types';
import { getMetricsTracer } from './otel-web.bootstrap';
import { PageNameNormalizer } from './page-name.normalizer';

/**
 * Tracks Angular router navigations and converts each successful one into a
 * single OTEL span shaped like:
 *
 *   tracer.startSpan('metrics.performance.page_load', {
 *     startTime: <NavigationStart wall clock>,
 *     attributes: { tenant.id, event.type=PAGE_LOAD, page.name }
 *   });
 *   span.end(<NavigationEnd>);
 *
 * Why NavigationEnd (and not ApplicationRef.isStable):
 *   ApplicationRef.isStable in Angular 16+ is driven by PendingTasksToken,
 *   which never empties in apps with persistent timers (MSAL token refresh,
 *   long-poll subscriptions, indeterminate progress bars, etc.). That makes
 *   isStable an unreliable "page is interactive" signal in practice. Ending
 *   on NavigationEnd matches the standard SPA page-load definition used by
 *   Datadog RUM, GA4, and New Relic: route resolved + resolvers complete.
 *   Resolvers (e.g. `tenantBrandingResolver`) must finish before NavigationEnd
 *   fires, so their durations are correctly included in the metric.
 *
 * On `NavigationCancel` / `NavigationError`, the span status is set to ERROR
 * and ended; the processor then drops it (success-only policy).
 */
@Injectable({ providedIn: 'root' })
export class MetricsRouterTracker implements OnDestroy {
  private static readonly SPAN_NAME = `${SPA_PERF_SPAN_PREFIX}page_load`;

  private readonly normalizer = new PageNameNormalizer();

  private routerSub: Subscription | null = null;
  private currentSpan: Span | null = null;
  private tracerOverride: Tracer | null = null;
  private tenantIdProvider: () => string | null = () => sessionStorage.getItem('tenantId');

  constructor(private readonly router: Router) {}

  /**
   * Public test seam: allows specs to inject a stub tracer or tenant provider.
   * Production code uses the defaults.
   */
  public configureForTest(opts: { tracer?: Tracer; tenantIdProvider?: () => string | null }): void {
    if (opts.tracer) {
      this.tracerOverride = opts.tracer;
    }
    if (opts.tenantIdProvider) {
      this.tenantIdProvider = opts.tenantIdProvider;
    }
  }

  public start(): void {
    if (this.routerSub) {
      return;
    }
    this.routerSub = this.router.events.subscribe((event) => this.onRouterEvent(event));
  }

  public stop(): void {
    this.routerSub?.unsubscribe();
    this.routerSub = null;
    this.cancelCurrent('tracker.stopped');
  }

  public ngOnDestroy(): void {
    this.stop();
  }

  private onRouterEvent(event: RouterEvent): void {
    if (event instanceof NavigationStart) {
      this.beginNav();
      return;
    }
    if (event instanceof NavigationEnd) {
      this.endNav();
      return;
    }
    if (event instanceof NavigationCancel || event instanceof NavigationError) {
      this.cancelCurrent('navigation.failed');
    }
  }

  private beginNav(): void {
    // If a previous nav's span is still pending (rare: rapid double-nav),
    // mark it cancelled so the processor drops it.
    this.cancelCurrent('navigation.preempted');

    const tracer = this.tracerOverride ?? getMetricsTracer();
    const tenantId = this.tenantIdProvider();

    // Without a tenant we still create the span (so processor can decide), but
    // attach the attribute only if non-null.
    this.currentSpan = tracer.startSpan(MetricsRouterTracker.SPAN_NAME, {
      attributes: tenantId ? { [SPA_OTEL_ATTR.TENANT_ID]: tenantId } : {},
    });
  }

  private endNav(): void {
    if (!this.currentSpan) {
      return;
    }
    const span = this.currentSpan;
    // Capture page name now: route snapshot is fully resolved at NavigationEnd.
    const pageName = this.normalizer.fromRouteSnapshot(this.router.routerState.snapshot.root);
    span.setAttribute(SPA_OTEL_ATTR.PAGE_NAME, pageName);
    span.setAttribute(SPA_OTEL_ATTR.EVENT_TYPE, 'PAGE_LOAD');
    span.end();
    this.currentSpan = null;
  }

  private cancelCurrent(reason: string): void {
    if (!this.currentSpan) {
      return;
    }
    this.currentSpan.setStatus({
      code: SpanStatusCode.ERROR,
      message: reason,
    });
    this.currentSpan.end();
    this.currentSpan = null;
  }
}
